import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Loader2 } from 'lucide-react';
import { useValidatePromoCode, useApplyPromoCode } from '../../../utils/api-hooks/subscription';

const PromoCodeInput = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [validationData, setValidationData] = useState(null);

  const validateMutation = useValidatePromoCode();
  const applyMutation = useApplyPromoCode();

  const handleValidate = async () => {
    if (!code.trim()) {
      setValidationStatus('error');
      setValidationMessage(t('subscription.paywall.promoCode.enterCode'));
      setValidationData(null);
      return;
    }

    try {
      const result = await validateMutation.mutateAsync(code);

      if (result && result.status === false && result.valid === false) {
        setValidationStatus('error');
        setValidationMessage(result.message || t('subscription.paywall.promoCode.invalid'));
        setValidationData(null);
        return;
      }

      if (result.valid) {
        setValidationStatus('success');
        setValidationMessage(result.description || t('subscription.paywall.promoCode.valid'));
        setValidationData(result);
      } else {
        setValidationStatus('error');
        setValidationMessage(result.message || t('subscription.paywall.promoCode.invalid'));
        setValidationData(null);
      }
    } catch (error) {
      setValidationStatus('error');
      setValidationMessage(t('subscription.paywall.promoCode.validateFailed'));
      setValidationData(null);
    }
  };

  const handleApply = async () => {
    if (!code.trim()) return;

    try {
      const result = await applyMutation.mutateAsync(code);

      if (result.status) {
        setValidationStatus('applied');
        setValidationMessage(
          result.promo_message ||
            `${t('subscription.paywall.promoCode.success')} ${result.description || ''}`.trim()
        );

        if (onSuccess) {
          await onSuccess();
        }

        setTimeout(() => {
          setValidationStatus(null);
          setValidationMessage('');
          setValidationData(null);
          setCode('');
        }, 3000);
      } else {
        setValidationStatus('error');
        setValidationMessage(result.message || t('subscription.paywall.promoCode.applyFailed'));
      }
    } catch (error) {
      setValidationStatus('error');
      setValidationMessage(t('subscription.paywall.promoCode.applyFailed'));
    }
  };

  const handleChange = (e) => {
    setCode(e.target.value.toUpperCase());
    setValidationStatus(null);
    setValidationMessage('');
  };

  const isLoading = validateMutation.isPending || applyMutation.isPending;

  return (
    <div className="h-full w-full min-w-0 bg-white dark:bg-[#1d1d1d] border border-[#E8EBE9] dark:border-[#30363d] rounded-[12px] p-[16px] sm:p-[28px] flex flex-col shadow-[0_1px_2px_rgba(16,24,40,0.06),0_12px_32px_-12px_rgba(16,24,40,0.16)] dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10">
      <div className="mb-[20px] text-center">
        <h3 className="font-['Inter'] font-semibold text-[18px] text-[#282A29] dark:text-[#f0f5f2] mb-[6px]">
          {t('subscription.paywall.promoCode.title')}
        </h3>
        <p className="font-['Inter'] text-[14px] text-[#828382] dark:text-[#8b949e]">
          {t('subscription.paywall.promoCode.subtitle')}
        </p>
      </div>

      <label
        htmlFor="promo-code-input"
        className="block min-w-0 max-w-full whitespace-normal [overflow-wrap:anywhere] mb-[8px] font-['Inter'] text-[14px] text-[#828382] dark:text-[#8b949e]"
      >
        {t('subscription.paywall.promoCode.placeholder')}
      </label>
      <div className="flex flex-col gap-[12px] mb-[12px]">
        <input
          id="promo-code-input"
          type="text"
          value={code}
          onChange={handleChange}
          disabled={isLoading}
          className="w-full min-w-0 bg-white dark:bg-[#191919] border border-[#E8EBE9] dark:border-[#30363d] rounded-[8px] px-[12px] sm:px-[16px] py-[12px] font-['Inter'] text-[15px] text-[#282A29] dark:text-[#f0f5f2] placeholder-[#828382] focus:outline-none focus:border-bloom-green transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />

        {validationStatus === 'success' && validationStatus !== 'applied' ? (
          <button
            type="button"
            onClick={handleApply}
            disabled={isLoading}
            className="w-full min-w-0 [overflow-wrap:anywhere] bg-bloom-green text-white px-[24px] py-[12px] rounded-[8px] font-['Inter'] font-medium text-[15px] hover:bg-bloom-green-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applyMutation.isPending ? (
              <Loader2 size={20} className="animate-spin mx-auto" />
            ) : (
              t('subscription.paywall.promoCode.activate')
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleValidate}
            disabled={isLoading || !code.trim()}
            className="w-full min-w-0 [overflow-wrap:anywhere] bg-bloom-green text-white px-[28px] py-[12px] rounded-[8px] font-['Inter'] font-medium text-[15px] hover:bg-bloom-green-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {validateMutation.isPending ? (
              <Loader2 size={20} className="animate-spin mx-auto" />
            ) : (
              t('subscription.paywall.promoCode.validate')
            )}
          </button>
        )}
      </div>

      {validationMessage && (
        <div
          className={`flex items-start gap-[8px] p-[12px] rounded-[8px] ${
            validationStatus === 'success' || validationStatus === 'applied'
              ? 'bg-bloom-green/10 border border-bloom-green/20'
              : 'bg-[#ff6b6b]/10 border border-[#ff6b6b]/20'
          }`}
        >
          {(validationStatus === 'success' || validationStatus === 'applied') ? (
            <Check size={16} className="text-bloom-green flex-shrink-0 mt-[2px]" />
          ) : (
            <X size={16} className="text-[#ff6b6b] flex-shrink-0 mt-[2px]" />
          )}
          <span
            className={`min-w-0 break-words font-['Inter'] text-[14px] ${
              validationStatus === 'success' || validationStatus === 'applied'
                ? 'text-bloom-green'
                : 'text-[#ff6b6b]'
            }`}
          >
            {validationMessage}
          </span>
        </div>
      )}
    </div>
  );
};

export default PromoCodeInput;
